<script>
import { Radar, mixins as ChartMixins } from "vue-chartjs";
import { mapGetters } from "vuex";

import { PartyColor } from "@/utils/PartyColor";

export default {
  name: "SmartSpider",
  extends: Radar,
  mixins: [PartyColor, ChartMixins.reactiveData],
  props: {
    showUser: {
      type: Boolean,
      required: false,
      default: true
    },
    candidateId: {
      type: Number,
      required: false,
      default: null
    },
    listId: {
      type: Number,
      required: false,
      default: null
    },
    usePartyColor: {
      type: Boolean,
      default: true
    },
    customColor: {
      type: String,
      required: false
    },
    customSmartSpider: {
      type: Object,
      required: false
    }
  },
  data: () => ({
    labels: ["", "", "", "", "", "", "", ""],
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,
      layout: {
        padding: {
          left: -15,
          right: -15,
          top: -17,
          bottom: -16
        }
      },
      scale: {
        ticks: {
          display: false,
          suggestedMin: 0,
          suggestedMax: 1,
          stepSize: 0.2,
          minor: {
            color: "white"
          },
          major: {
            color: "white"
          }
        },
        angleLines: false,
        gridLines: false
      },
      legend: {
        display: false
      },
      tooltips: {
        enabled: false
      },
      elements: {
        point: {
          pointRadius: "0",
          hoverRadius: "0"
        },
        line: {
          borderWidth: "4",
          borderJoinStyle: "round"
        }
      }
    },
    loadCompleted: false
  }),
  async mounted() {
    this.options.onResize = () => this.updateChartData();
    await new Promise(r => setTimeout(r, 50));
    if (this._isDestroyed) {
      return;
    }
    this.updateChartData();
    this.renderChart(this.chartData, this.options);
    await new Promise(r => setTimeout(r, 50));
    if (this._isDestroyed) {
      return;
    }
    this.updateChartData();
  },
  computed: {
    ...mapGetters("questionnaire", ["smartSpider"]),
    ...mapGetters("candidate", { getCandidate: "candidate" }),
    candidate() {
      return this.candidateId != null
        ? this.$store.getters["candidate/candidate"](this.candidateId)
        : null;
    },
    list() {
      return this.listId != null
        ? this.$store.getters["list/list"](this.listId)
        : null;
    }
  },
  methods: {
    getUserGradient() {
      /**
       * @type {HTMLCanvasElement}
       */
      const canvas = this.$refs.canvas;
      if (!canvas) {
        return "";
      }
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const x = width / 2;
      const y = height / 2;
      const radius = width / 2;
      const gradient = canvas
        .getContext("2d")
        .createRadialGradient(x, y, 0, x, y, radius);
      // gradient.addColorStop(0, "#FFFFFFFF");
      // gradient.addColorStop(1, "#FFFFFF88");
      gradient.addColorStop(0, "#FFFFFFFF");
      gradient.addColorStop(1, "#FFFFFF88");
      return gradient;
    },
    updateChartData() {
      let datasets = [];
      this.loadCompleted = true;

      if (this.showUser != false) {
        const smartSpider = this.smartSpider || this.customSmartSpider;
        let data = smartSpider
          ? this.getSmartSpiderValues(smartSpider)
          : [0, 0, 0, 0, 0, 0, 0, 0];
        datasets.push({
          label: "My Political Beliefs",
          order: 0,
          backgroundColor: this.getUserGradient(),
          borderColor: "rgba(255,255,255,1)",
          pointRadius: "0",
          data: data
        });
      }

      if (this.candidate != null) {
        const smartSpider =
          this.candidate.smartSpider || this.customSmartSpider;
        let data = smartSpider
          ? this.getSmartSpiderValues(smartSpider)
          : [0, 0, 0, 0, 0, 0, 0, 0];
        const candidatePartyColor = this.usePartyColor
          ? this.getPartyColor(this.candidate.partyId)
          : "#04f5f5";
        const color = this.customColor || candidatePartyColor;
        datasets.push({
          label: "Candidate Political Beliefs",
          order: 1,
          backgroundColor: color + "88",
          borderColor: color,
          pointRadius: "0",
          data: data
        });
      }

      if (this.list != null) {
        let [count, values] = this.list.candidates
          .map(entry => entry.id)
          .filter(id => {
            let candidate = this.getCandidate(id);
            if (!candidate.smartSpider) {
              this.loadCompleted = false;
            }
            return (
              candidate.smartSpider &&
              this.getSmartSpiderValues(candidate.smartSpider).reduce(
                (acc, val) => acc + val,
                0
              ) !== 0
            );
          })
          .reduce(
            ([count, values], id) => {
              let candidate = this.getCandidate(id);
              return [
                count + 1,
                values.map(
                  (value, index) =>
                    value +
                    (candidate.smartSpider
                      ? this.getSmartSpiderValues(candidate.smartSpider)[index]
                      : 0)
                )
              ];
            },
            [0, new Array(8).fill(0)]
          );

        let data;
        if (count !== 0) {
          data = values.map(value => value / count);
        } else {
          data = values;
        }

        let party = "";
        if (this.list.candidates.length !== 0) {
          let partyCounts = this.list.candidates
            .map(entry => entry.id)
            .reduce((acc, id) => {
              let candidate = this.getCandidate(id);
              if (acc[candidate.partyId] == undefined) {
                acc[candidate.partyId] = 1;
              } else {
                acc[candidate.partyId] += 1;
              }

              return acc;
            }, {});

          party = Object.entries(partyCounts).sort(([, a], [, b]) => b - a)[0];
          if (party !== undefined) {
            party = party[0];
          } else {
            party = "";
          }
        }

        datasets.push({
          label: "List Political Beliefs",
          order: 2,
          backgroundColor: this.getPartyColor(party) + "BB",
          borderColor: this.getPartyColor(party),
          pointRadius: "0",
          data
        });
      }

      this.chartData = {
        labels: this.labels,
        datasets
      };

      if (!this.loadCompleted) {
        setTimeout(this.updateChartData, 500);
      }
    },
    getSmartSpiderValues(smartSpider) {
      return [
        smartSpider["axis_1"] || 0,
        smartSpider["axis_2"] || 0,
        smartSpider["axis_3"] || 0,
        smartSpider["axis_4"] || 0,
        smartSpider["axis_5"] || 0,
        smartSpider["axis_6"] || 0,
        smartSpider["axis_7"] || 0,
        smartSpider["axis_8"] || 0
      ];
    }
  },
  watch: {
    candidateId() {
      this.updateChartData();
    },
    list() {
      this.updateChartData();
    },
    smartSpider() {
      this.updateChartData();
    }
  }
};
</script>
